import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode } from "html5-qrcode"
import { reportCameraOpened } from "../camera_nfc_conflict"

// Opens the device camera on demand and scans QR codes using html5-qrcode.
// Dispatches "qr:read" { code } on the controller element on success, then closes
// the scanner.
//
// A live stream, not a photo: decoding a still shot works only where
// BarcodeDetector is there, and the Android 8–10 handsets in the field ship
// without the Play Services barcode module. Dozens of frames a second is what
// makes a cheap camera land a read at all.
//
// Opening the camera costs this document its Web NFC — see camera_nfc_conflict.
// The screens keep both readers and the operator picks one per card; this only has
// to report the camera so the nfc-reader can say it went deaf.
//
// Targets:
//   reader      — element where html5-qrcode renders the camera view (must have an id)
//   openBtn     — shown when closed; hidden when open
//   scannerArea — hidden when closed; shown when open
//   status      — carries the reason the camera could not open
export default class extends Controller {
  static targets = ["reader", "openBtn", "scannerArea", "status"]

  #scanning = false

  connect() {
    this.scanner = new Html5Qrcode(this.readerTarget.id, {
      experimentalFeatures: { useBarCodeDetectorIfSupported: true }
    })
  }

  disconnect() {
    this.#scanning = false
    try { this.scanner?.stop().catch(() => {}) } catch {}
    this.scanner = null
  }

  open() {
    if (this.#scanning) return

    this.statusTarget.hidden = true
    this.openBtnTarget.hidden = true
    this.scannerAreaTarget.hidden = false
    this.#scanning = true
    this.#startScanner()
  }

  close() {
    this.#scanning = false
    this.openBtnTarget.hidden = false
    this.scannerAreaTarget.hidden = true
    try { this.scanner?.stop().catch(() => {}) } catch {}
  }

  // private

  #startScanner() {
    const config = {
      // 15 starved the decode loop on old phones: without BarcodeDetector (the
      // Play Services module is missing on plenty of Android 8–10 handsets)
      // html5-qrcode falls back to a JS decoder that cannot keep that pace.
      fps: 10,
      qrbox: (w, h) => { const s = Math.floor(Math.min(w, h) * 0.7); return { width: s, height: s } },
      // Overrides the first argument of start() — html5-qrcode prefers
      // videoConstraints whenever it is present and valid.
      //
      // Cheap old cameras default to fixed focus and a low-resolution stream, so a
      // printed QR held close is permanently blurred and carries too few pixels per
      // module to decode: the camera opens, the preview looks fine, nothing ever
      // reads. focusMode goes inside `advanced` on purpose — there it is best
      // effort and a device without it just ignores it, while at the top level an
      // unsupported constraint rejects the whole stream. Same reasoning for
      // `ideal` over `exact` everywhere here.
      //
      // aspectRatio is deliberately absent: a device that cannot honour it fails
      // with OverconstrainedError, which is precisely the failure this screen used
      // to swallow.
      videoConstraints: {
        facingMode: { ideal: "environment" },
        width:      { ideal: 1280 },
        height:     { ideal: 720 },
        advanced:   [{ focusMode: "continuous" }]
      },
      experimentalFeatures: { useBarCodeDetectorIfSupported: true }
    }

    this.scanner.start(
      { facingMode: "environment" },
      config,
      (code) => {
        if (!this.#scanning) return

        this.close()
        this.element.dispatchEvent(new CustomEvent("qr:read", {
          bubbles: true,
          detail: { code: code.trim() }
        }))
      },
      () => {}
    ).then(reportCameraOpened).catch((error) => {
      // Never fail silently. The old catch closed the scanner and said nothing, so
      // "I tap the button and nothing happens" was all anyone could report back.
      this.statusTarget.textContent = this.#cameraErrorMessage(error)
      this.statusTarget.hidden = false
      this.close()
    })
  }

  // html5-qrcode rejects with a bare string in some paths, so an absent `name`
  // falls through to the generic message rather than throwing here.
  #cameraErrorMessage(error) {
    switch (error?.name) {
      case "NotAllowedError":
        return "Permissão de câmera negada. Autorize a câmera e toque de novo."
      case "NotFoundError":
        return "Nenhuma câmera encontrada neste aparelho."
      case "NotReadableError":
        return "Câmera em uso por outro aplicativo. Feche-o e toque de novo."
      case "OverconstrainedError":
        return "A câmera deste aparelho não aceita o modo de leitura. Digite o código."
      default:
        return "Não consegui abrir a câmera. Digite o código."
    }
  }
}
